<template>
    <pc-table
        data-test-role="table-schema"
        v-bind:columns="schemaColumns"
        v-bind:items="internalValue"
        key-prop="Key"
        class="center-items"
        hide-search>
        <template v-slot:FieldType="row">
            <div>{{ findTypeNameForRow(row) }}</div>
        </template>
        <template v-slot:ExternalName="row">
            <pc-input
                v-bind:model-value="internalValue[row.Key].ExternalName"
                v-bind:disabled="!row.IsIncluded || isReadOnly"
                data-test-role="input-external-name"
                class="w-100"
                v-bind:validator="columnNameValidator"
                v-bind:alt-label="externalColumnHeader"
                v-on:update:model-value="onInputColumnName(row.Key, $event)"
                v-on:validated="onValidated(`Value${row.Key}`, $event)" />
        </template>
    </pc-table>
</template>
<script>
    import { NAMESPACE, KEYS } from "./i18n/externalColumnMappingEditor.i18n";
    import { KEYS as SHARED_KEYS, NAMESPACE as SHARED_NAMESPACE } from "./i18n/customListSharedStrings.i18n";
    import { mapI18n } from "Util/i18n";
    import { EXTERNAL_COLUMN_TYPES } from "Util/externalDataUtil.js";
    import { areStringsEqualIgnoreCase } from "Util/util.js";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { ValidationHub } from "Validation/validationHub";

    export default {
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        props: {
            modelValue: { type: Array, required: true },
            disallowedNameCharacters: { type: Array, required: true },
            isReadOnly: { type: Boolean, required: true }
        },
        emits: ["update:modelValue", "validated"],
        data() {
            return {
                validationHub: new ValidationHub(),
                validationResults: {}
            };
        },
        computed: {
            internalValue: {
                get() {
                    return this.modelValue != null ? this.modelValue : [];
                },
                set(value) {
                    this.$emit("update:modelValue", value);
                }
            },
            schemaColumns() {
                const result = [
                    {
                        // Default invisible column for ordering
                        property: "Key",
                        orderable: true,
                        searchable: false,
                        visible: false
                    },
                    {
                        header: this.internalColumnHeader,
                        property: "InternalName",
                        orderable: false,
                        searchable: false,
                        visible: true,
                        classes: "col-md-3 col-xl-4 px-0"
                    },
                    {
                        header: this.importHeader,
                        property: "IsIncluded",
                        orderable: false,
                        searchable: false,
                        visible: true,
                        displayValue: this.booleanDisplayValue,
                        classes: "col-md-2 col-xl-1 px-0"
                    },
                    {
                        header: this.keyColumnHeader,
                        property: "IsPartOfIdentifier",
                        orderable: false,
                        searchable: false,
                        visible: true,
                        displayValue: this.booleanDisplayValue,
                        classes: "col-md-2 col-xl-1 px-0"
                    },
                    {
                        header: this.typeHeader,
                        property: "FieldType",
                        orderable: false,
                        searchable: false,
                        visible: true,
                        classes: "col-md-2 col-xl-2 px-0"
                    },
                    {
                        header: this.externalColumnHeader,
                        property: "ExternalName",
                        orderable: false,
                        searchable: false,
                        visible: true,
                        classes: "col-md-3 col-xl-4 px-0"
                    }
                ];

                return result;
            },
            columnNameValidator() {
                return getValidator(this.$app, [
                    VALIDATOR_TYPES.required,
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: (val) => this.validateInvalidChars(val, false),
                        errorMsg: this.invalidCharactersInColumnNameErrorMessage
                    },
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: this._isColumnNameNotDuplicate,
                        errorMsg: this.duplicateColumnNameErrorMessage,
                        skipTrimValue: true
                    }
                ]);
            },
            hasErrors() {
                for (const key in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(key)) {
                        if (this.validationResults[key].hasError) {
                            return true;
                        }
                    }
                }
                return false;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    invalidCharactersInColumnNameErrorMessage() {
                        return {
                            key: KEYS.invalidCharactersInColumnNameErrorMessageTemplate,
                            values: { invalid: this.disallowedNameCharacters }
                        };
                    }
                },
                NAMESPACE
            ),
            ...mapI18n(SHARED_KEYS, SHARED_NAMESPACE)
        },
        watch: {},
        methods: {
            onCreated() {
                if (this.validationHub) {
                    this.validationHub.register(this.internalId, this);
                }
            },
            onInput() {
                this.$emit("update:modelValue", this.externalColumns);
            },
            onLoaded() {
                this.externalColumns = this.modelValue;
            },
            booleanDisplayValue(val) {
                return val ? this.yesLabel : "";
            },
            findTypeNameForRow(row) {
                const typeId = row.FieldType;
                const typeDesc = EXTERNAL_COLUMN_TYPES[typeId];

                // Get i18n string for "StringTypeLabel", "IntegerTypeLabel"...
                // constructed by typeDesc, which should be "String", or "Integer", etc.
                return this[`typeLabel${typeDesc}`];
            },
            validateInvalidChars(value) {
                // Get the correct character set for whether this is a list name of column name
                const invalidCharacters = this.disallowedNameCharacters || [];
                const name = value || "";

                return invalidCharacters.filter((c) => name.includes(c)).length === 0;
            },
            onValidated(key, result) {
                const updatedResults = JSON.parse(JSON.stringify(this.validationResults));
                updatedResults[key] = result;
                this.validationResults = updatedResults;
                this.emitValidation();
            },
            delayValidate() {
                setTimeout(() => this.validationHub.validate(), 0);
            },
            onInputColumnName(key, colName) {
                this.internalValue[key].ExternalName = colName;
                this.delayValidate();
            },
            summarizeErrors() {
                const errors = [];

                if (this.hasParsingError) {
                    errors.push(this.parsingErrorMessage);
                }
                for (const key in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(key)) {
                        const validationResult = this.validationResults[key];
                        if (validationResult.hasError) {
                            if (!errors.includes(validationResult.error)) {
                                errors.push(validationResult.error);
                            }
                        }
                    }
                }

                return errors;
            },
            emitValidation() {
                const errors = this.summarizeErrors();
                const hasErrors = errors.length > 0;

                this.$emit("validated", {
                    error: errors.join(", "),
                    hasError: hasErrors,
                    type: "custom"
                });
            },
            _isColumnNameNotDuplicate(value) {
                const trimmed = value.trim();
                let count = 0;
                for (let i = 0; i < this.internalValue.length; i++) {
                    if (areStringsEqualIgnoreCase(this.internalValue[i].ExternalName.trim(), trimmed)) {
                        count++;
                    }
                }

                return count < 2;
            }
        }
    };
</script>
